import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from '@shared/api/client';
import { recordEvent } from '@shared/api/events';
import { getClientId } from '@shared/clientId';
import type { SubmissionRequest } from '@shared/types/api';

import { useDdwConfig } from './useDdwConfig';
import {
  clearDdwState,
  emptyDdwState,
  loadDdwState,
  persistDdwState,
} from './state';
import { adoptionConfidence, calcBaselineTax, projectVendorTax, valueAtRisk } from './calc';
import { Welcome } from './components/Welcome';
import { Phase1Wizard } from './components/Phase1Wizard';
import { VendorHub } from './components/VendorHub';
import { Phase2Scorecard } from './components/Phase2Scorecard';
import { Reports, type ReportMode } from './components/Reports';
import type {
  DdwLens,
  DdwPayload,
  DdwResearch,
  DdwScore,
  DdwState,
  DdwSubmissionVendor,
  DdwVendor,
} from './types';
import './ddw.css';

type Screen = 'welcome' | 'wizard' | 'hub' | 'scorecard' | 'report';

const SUBMISSION_ID_KEY = 'motr_ddw_submission_id';

/**
 * Top-level Due Diligence Workbench surface. Owns the entire state
 * machine — screen routing, persistence, submission. Children are
 * presentational; they receive callbacks that return back here.
 *
 * The state lives in localStorage (motr-ddw-state-v1) and the lens
 * mirror lives at motr-lens-v1 (shared with Plan Builder).
 */
export function DdwTool() {
  const { config } = useDdwConfig();
  const [state, setState] = useState<DdwState>(() => loadDdwState());
  const [screen, setScreen] = useState<Screen>('welcome');
  const [scoringVendorId, setScoringVendorId] = useState<string | null>(null);
  const [reportMode, setReportMode] = useState<ReportMode>('single');
  const [reportVendorId, setReportVendorId] = useState<string | undefined>(undefined);
  const [research, setResearch] = useState<DdwResearch | null>(null);
  const [wizardInitialStep, setWizardInitialStep] = useState(1);
  // Wall-clock from mount to first phase1_complete; stamped on submission.
  const startTimeRef = useRef<number>(Date.now());

  // Fire 'view' event once on mount.
  useEffect(() => {
    void recordEvent({ tool_id: 'ddw', event_type: 'view' });
  }, []);

  // Fire phase-boundary events. Each fires once per session via a
  // ref-tracked map so re-renders don't re-emit.
  const eventsFiredRef = useRef<Record<string, boolean>>({});
  const fireEventOnce = useCallback(
    (key: string, eventType: string, data?: Record<string, unknown>) => {
      if (eventsFiredRef.current[key]) return;
      eventsFiredRef.current[key] = true;
      void recordEvent({ tool_id: 'ddw', event_type: eventType, data });
    },
    [],
  );

  const persist = useCallback((next: DdwState) => {
    persistDdwState(next);
    setState({ ...next });
  }, []);

  const updateLens = useCallback(
    (updater: (lens: DdwLens) => DdwLens) => {
      setState((prev) => {
        const next: DdwState = { ...prev, lens: updater(prev.lens) };
        persistDdwState(next);
        return next;
      });
    },
    [],
  );

  // Submission helper — fires whenever Phase 1 finishes or a vendor is
  // saved. The server upserts on client_submission_id so repeated calls
  // update the same submission row.
  const submit = useCallback(
    async (current: DdwState, currentResearch: DdwResearch | null) => {
      const submissionId = readSubmissionId() ?? mintSubmissionId();
      const payload = buildPayload(current, currentResearch, config);
      const req: SubmissionRequest<DdwPayload> = {
        tool_id: 'ddw',
        schema_version: 'v1',
        client_submission_id: submissionId,
        submitted_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || undefined,
        payload,
      };
      try {
        await api.submit(getClientId(), req);
      } catch (err) {
        // Validation errors surface immediately; transient failures are
        // queued for retry by the api client. Either way, don't block
        // the UI on submission outcomes.
        if (!(err instanceof ApiError) || err.isRetryable) {
          // queued or transient — already handled by client
          return;
        }
        // Hard failure — log once for diagnostics.
        // eslint-disable-next-line no-console
        console.warn('[ddw] submission validation failed', err);
      }
    },
    [config],
  );

  // ─── Screen navigation ──────────────────────────────────────

  const handleWelcomePrimary = useCallback(() => {
    if (state.phase1Complete) {
      setScreen('hub');
    } else if (state.lens.jobStatement.trim().length > 0) {
      setWizardInitialStep(1);
      setScreen('wizard');
    } else {
      setWizardInitialStep(1);
      setScreen('wizard');
    }
  }, [state]);

  const handleStartOver = useCallback(() => {
    if (
      !confirm(
        "Start over? Your lens, your vendors, and your scores will all be deleted. This cannot be undone.",
      )
    )
      return;
    clearDdwState();
    try {
      localStorage.removeItem(SUBMISSION_ID_KEY);
    } catch {
      /* ignore */
    }
    const fresh = emptyDdwState();
    persist(fresh);
    setResearch(null);
    setScreen('welcome');
  }, [persist]);

  const handleFinishPhase1 = useCallback(() => {
    setState((prev) => {
      const total = Math.round(calcBaselineTax(prev.lens, config.calc).total);
      const next: DdwState = {
        ...prev,
        phase1Complete: true,
        lens: { ...prev.lens, tax: { ...prev.lens.tax, calculatedAnnual: total } },
      };
      persistDdwState(next);
      const completionSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
      fireEventOnce('phase1', 'phase1_complete', {
        completion_seconds: completionSeconds,
        baseline_tax: total,
      });
      void submit(next, research);
      return next;
    });
    setScreen('hub');
  }, [config, fireEventOnce, research, submit]);

  const handleAddVendor = useCallback(
    (name: string, category: string) => {
      setState((prev) => {
        const newVendor: DdwVendor = {
          id: genVendorId(),
          name,
          category,
          addedAt: new Date().toISOString(),
          scored: false,
          score: null,
        };
        const next: DdwState = { ...prev, vendors: [...prev.vendors, newVendor] };
        persistDdwState(next);
        // Fire on every add (not once-only) — vendor count is the
        // signal we want to mine. Use a unique key per vendor index.
        void recordEvent({
          tool_id: 'ddw',
          event_type: 'vendor_added',
          data: { vendor_count: next.vendors.length, category },
        });
        return next;
      });
    },
    [],
  );

  const handleRemoveVendor = useCallback(
    (vendorId: string) => {
      setState((prev) => {
        const next: DdwState = { ...prev, vendors: prev.vendors.filter((v) => v.id !== vendorId) };
        persistDdwState(next);
        return next;
      });
    },
    [],
  );

  const handleScoreVendor = useCallback((vendorId: string) => {
    setScoringVendorId(vendorId);
    setScreen('scorecard');
  }, []);

  const handleSaveScore = useCallback(
    (score: DdwScore, action: 'return' | 'addAnother') => {
      if (!scoringVendorId) return;
      setState((prev) => {
        const next: DdwState = {
          ...prev,
          vendors: prev.vendors.map((v) =>
            v.id === scoringVendorId
              ? { ...v, score, scored: true, scoredAt: new Date().toISOString() }
              : v,
          ),
        };
        persistDdwState(next);
        const scoredCount = next.vendors.filter((v) => v.scored).length;
        void recordEvent({
          tool_id: 'ddw',
          event_type: 'vendor_scored',
          data: {
            scored_count: scoredCount,
            gut: score.gut,
            ownership: score.ownership,
          },
        });
        void submit(next, research);
        return next;
      });
      setScoringVendorId(null);
      setScreen('hub');
      // Note: Vendor Hub re-renders with the form opened automatically
      // by users clicking "+ Add a vendor"; "Save & add another" mirrors
      // the prototype's behavior of returning to the hub then opening
      // the add form. The hub's local state owns the "adding" flag, so
      // the simplest implementation here is to send the buyer back to
      // the hub and let them tap +Add. (Acceptable per prototype UX.)
      void action;
    },
    [research, scoringVendorId, submit],
  );

  const handleClearScore = useCallback((vendorId: string) => {
    setState((prev) => {
      const next: DdwState = {
        ...prev,
        vendors: prev.vendors.map((v) =>
          v.id === vendorId ? { ...v, score: null, scored: false, scoredAt: undefined } : v,
        ),
      };
      persistDdwState(next);
      return next;
    });
    setScoringVendorId(null);
    setScreen('hub');
  }, []);

  const handleCancelScore = useCallback(() => {
    setScoringVendorId(null);
    setScreen('hub');
  }, []);

  const handleOpenReport = useCallback((mode: ReportMode, vendorId?: string) => {
    setReportMode(mode);
    setReportVendorId(vendorId);
    setScreen('report');
  }, []);

  const handleBackFromReport = useCallback(() => {
    setReportVendorId(undefined);
    setScreen('hub');
  }, []);

  const handleReviseLens = useCallback(() => {
    setWizardInitialStep(1);
    setScreen('wizard');
  }, []);

  const handleResearchSubmit = useCallback(
    (answers: DdwResearch) => {
      setResearch(answers);
      void submit(state, answers);
    },
    [state, submit],
  );
  const handleResearchDismiss = useCallback(() => {
    setResearch({ dismissed: true, submittedAt: new Date().toISOString() });
  }, []);

  // Apply admin var overrides (from config.vars) at the document root,
  // matching the prototype's brand-override behavior.
  useEffect(() => {
    if (!config.vars) return;
    const root = document.documentElement;
    const previous: Array<[string, string]> = [];
    for (const [k, v] of Object.entries(config.vars)) {
      previous.push([k, root.style.getPropertyValue(k)]);
      root.style.setProperty(k, v);
    }
    return () => {
      for (const [k, v] of previous) {
        if (v) root.style.setProperty(k, v);
        else root.style.removeProperty(k);
      }
    };
  }, [config.vars]);

  const scoringVendor = useMemo(
    () => state.vendors.find((v) => v.id === scoringVendorId) ?? null,
    [state.vendors, scoringVendorId],
  );

  return (
    <div className="ddw">
      <div className="ddw-subnav">
        <span className="ddw-subnav-meta">{config.content.navMeta}</span>
        <a className="ddw-subnav-link" href="/plan-builder">Plan Builder</a>
      </div>

      {screen === 'welcome' && (
        <Welcome
          state={state}
          content={config.content}
          onPrimary={handleWelcomePrimary}
          onStartOver={handleStartOver}
        />
      )}

      {screen === 'wizard' && (
        <Phase1Wizard
          lens={state.lens}
          config={config}
          initialStep={wizardInitialStep}
          onChange={updateLens}
          onFinish={handleFinishPhase1}
        />
      )}

      {screen === 'hub' && (
        <VendorHub
          lens={state.lens}
          vendors={state.vendors}
          config={config}
          research={research}
          onReviseLens={handleReviseLens}
          onAddVendor={handleAddVendor}
          onRemoveVendor={handleRemoveVendor}
          onScoreVendor={handleScoreVendor}
          onOpenReport={handleOpenReport}
          onSubmitResearch={handleResearchSubmit}
          onDismissResearch={handleResearchDismiss}
        />
      )}

      {screen === 'scorecard' && scoringVendor && (
        <Phase2Scorecard
          vendor={scoringVendor}
          lens={state.lens}
          config={config}
          onSave={handleSaveScore}
          onCancel={handleCancelScore}
          onClear={handleClearScore}
        />
      )}

      {screen === 'report' && (
        <Reports
          mode={reportMode}
          vendorId={reportVendorId}
          lens={state.lens}
          vendors={state.vendors}
          config={config}
          onBack={handleBackFromReport}
        />
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function genVendorId(): string {
  return 'v-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
}

function readSubmissionId(): string | null {
  try {
    return localStorage.getItem(SUBMISSION_ID_KEY);
  } catch {
    return null;
  }
}

function mintSubmissionId(): string {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : 'sub-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  try {
    localStorage.setItem(SUBMISSION_ID_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

function buildPayload(
  state: DdwState,
  research: DdwResearch | null,
  config: ReturnType<typeof useDdwConfig>['config'],
): DdwPayload {
  const vendors: DdwSubmissionVendor[] = state.vendors.map((v) => ({
    id: v.id,
    name: v.name,
    category: v.category || undefined,
    addedAt: v.addedAt,
    scored: v.scored,
    scoredAt: v.scoredAt,
    score: v.score,
    projection: v.scored && v.score ? projectVendorTax(state.lens, v.score, config) : null,
    adoption_confidence: v.scored && v.score
      ? adoptionConfidence(v.score, config.adoptionQuestions)
      : null,
    value_at_risk: v.scored && v.score ? valueAtRisk(state.lens, v.score, config) : null,
  }));

  return {
    version: 'v1',
    lens: state.lens,
    vendors,
    phase1Complete: state.phase1Complete,
    research,
  };
}
